export interface Location {
    id: number;
    name: string;
}

export interface TableClientUser {
    clients: { id: number };
    role: string;
    locationIds?: number[];
}

export interface TableUser {
    id: number;
    email: string;
    emailConfirmed: boolean;
    firstName: string;
    lastName: string;
    systemRole: string;
    clientUsers: TableClientUser[];
}

export const getRoleName = (
    user: TableUser,
    clientId: number,
    t: (key: string) => string
): string => {
    const isUserOptoscaleAdmin = user.systemRole === 'ADMIN';

    if (isUserOptoscaleAdmin) {
        return t('userAccess.common.optoscaleAdmin');
    }
    if (clientId) {
        const clientRole = user.clientUsers.find((role) => role.clients.id === clientId);

        if (clientRole) {
            if (clientRole.role === 'ADMIN') {
                return t('userAccess.common.clientAdmin');
            }
            if (clientRole.role === 'LOCATION_ADMIN') {
                return t('userAccess.common.locationAdmin');
            }
        }
    } else if (user?.clientUsers.length > 0) {
        if (user?.clientUsers[0].role === 'ADMIN') {
            return t('userAccess.common.clientAdmin');
        }
        if (user?.clientUsers[0].role === 'LOCATION_ADMIN') {
            return t('userAccess.common.locationAdmin');
        }
    }
    return '';
};

/** The user's membership at the client, or undefined when they have none there. */
function clientUserAt(user: TableUser, clientId: number): TableClientUser | undefined {
    return clientId ? user.clientUsers.find((c) => c.clients.id === clientId) : undefined;
}

/**
 * A sort key for the locations column that reads only the row: every location ranks first,
 * then a location admin by how many locations they were granted, then no access. It must not
 * depend on the client's locations, which load separately: the table caches a row's values
 * until the rows themselves change.
 */
export function locationAccessRank(user: TableUser, clientId: number): number {
    if (user.systemRole === 'ADMIN') return Number.MAX_SAFE_INTEGER;
    const cu = clientUserAt(user, clientId);
    if (cu?.role === 'ADMIN') return Number.MAX_SAFE_INTEGER;
    if (cu?.role === 'LOCATION_ADMIN') return cu.locationIds?.length ?? 0;
    return -1;
}

/**
 * "All locations" for an admin, "n of m" for a location admin, counting only the granted
 * locations the client has. Empty while the client's locations are not known, rather than
 * a count against none.
 */
export const getLocationSummary = (
    user: TableUser,
    clientId: number,
    clientLocationIds: readonly number[],
    t: (key: string, opts?: Record<string, unknown>) => string
): string => {
    if (user.systemRole === 'ADMIN') {
        return t('userAccess.allLocations');
    }

    const cu = clientUserAt(user, clientId);
    if (!cu) return '';

    if (cu.role === 'ADMIN') {
        return t('userAccess.allLocations');
    }

    if (cu.role === 'LOCATION_ADMIN') {
        if (clientLocationIds.length === 0) return '';
        const granted = new Set(cu.locationIds ?? []);
        const count = clientLocationIds.filter((id) => granted.has(id)).length;
        return t('userAccess.nOfM', { n: count, m: clientLocationIds.length });
    }

    return '';
};
